import './Pushes.sass';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pushesActions, pushesSelector } from '$store/pushes';
import { Push } from '$shared/components/Pushes/Push/Push';
import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import useSize from '@react-hook/size';
import { useDesktop } from '$hooks';
import { PushType } from '$store/pushes/interface';
import { showModal } from '$navigation/helper';
import { statReachGoal } from '$utils';
import router from '$navigation/router';
import { CHAT } from '$shared/constants/pages';

export const Pushes = () => {
  const { items } = useSelector(pushesSelector);
  const isDesktop = useDesktop();
  const lastPush = items[items.length - 1];
  const pushRef = useRef<HTMLDivElement>(null);
  const [_, height] = useSize(pushRef);
  const dispatch = useDispatch();
  const rate = isDesktop ? 1 : -1;
  const hiddenPosition = 500 * rate;
  const [hovered, setHovered] = useState(false);
  const [{ y }, api] = useSpring(() => ({ x: 0, y: hiddenPosition }));

  useEffect(() => {
    if (lastPush) {
      api.start({ y: 0 });
    }
  }, [lastPush]);

  const handleClose = useCallback(() => {
    if (!lastPush?.id || Math.abs(y.get()) > 6) return;
    dispatch(pushesActions.close(lastPush.id));
    statReachGoal(`push_close`, {
      type: lastPush.type,
      objectId: lastPush.objectId,
    });
  }, [lastPush, y]);

  const handleClick = useCallback(() => {
    if (!lastPush?.id || Math.abs(y.get()) > 6) return;
    switch (lastPush.type) {
      case PushType.LIVE:
        showModal('live' + lastPush.objectId);
        break;
      case PushType.MESSAGE:
        router.navigate(CHAT, { peerId: lastPush.objectId });
        break;
    }
    statReachGoal(`push_click`, {
      type: lastPush.type,
      objectId: lastPush.objectId,
    });
    dispatch(pushesActions.close(lastPush.id));
  }, [lastPush, y]);

  const bind = useDrag(({ down, movement: [mx, my] }) => {
    if (!down && my * rate > 0 && Math.abs(my) > height / 3) {
      return api.start({ y: hiddenPosition, onRest: handleClose });
    }
    api.start({
      y: down ? (my * -rate > 0 ? my / 3 : my) : 0,
      immediate: down,
    });
  });

  useEffect(() => {
    if (!lastPush) return;
    statReachGoal(`push_view`, {
      type: lastPush.type,
      objectId: lastPush.objectId,
    });
    const timeout = setTimeout(() => {
      !hovered && dispatch(pushesActions.close(lastPush.id));
    }, lastPush.ttl);
    return () => clearTimeout(timeout);
  }, [lastPush, hovered]);

  return (
    <div className="Pushes" {...bind()}>
      <animated.div className="Pushes__push" ref={pushRef} style={{ y }}>
        {lastPush && (
          <Push
            type={lastPush.type}
            onHover={setHovered}
            onClick={handleClick}
            onClose={handleClose}
            push={lastPush}
          />
        )}
      </animated.div>
    </div>
  );
};
